// KÖZÖS katalógus-kezelő elemek — a kiadás-kategóriák és az eszköz-kategóriák/-típusok „Kategóriák" sávja
// ugyanezekből épül (a 2. használatnál promotálva, nem másolva): a beúszó sáv héja (lista + alatta a
// felvevő/szerkesztő űrlap), a fa-vezetővonalak, a sor (név · számláló · ceruza · kuka) és a törlés-megerősítő.
// A hívó adja a domén-tudást (a sorok tartalmát, az űrlap mezőit, a mutációkat) — itt entitás-specifikus
// semmi nincs.
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormSheet } from '@/components/shared/form-sheet';
import { type TreeGuide } from '@/components/shared/tree-guides';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';

/**
 * FA-VEZETŐVONAL a gyerek-soron: függőleges szár + könyök, a sor FÜGGŐLEGES KÖZEPÉIG. Az utolsó gyerek szára
 * ott VÉGET is ér, a többié végigfut — így a testvérek egy összefüggő vonalon állnak, és látszik, hol zárul a
 * csoport. A `space-y` SZÁNDÉKOSAN hiányzik a listáról: egy soronkénti térköz megszaggatná a szárat.
 * SZINTENKÉNT egy oszlop: az alakot a `treeGuides` dönti el egy helyen — itt már csak rajzolunk.
 */
function TreeGuideCells({ guides }: { guides: readonly TreeGuide[] }): JSX.Element {
  return (
    <>
      {guides.map((guide, level) =>
        guide === 'blank' ? (
          // LEZÁRULT csoport: csak HELY, szár nélkül — a szélességnek maradnia kell, különben a mélyebb
          // sorok balra ugranának.
          <span key={level} aria-hidden className="ml-1 block w-3 shrink-0" />
        ) : (
          <span key={level} aria-hidden className="relative ml-1 block w-3 shrink-0 self-stretch">
            <span
              className={`bg-border absolute left-0 top-0 w-px ${
                guide === 'elbow' ? 'h-1/2' : 'h-full'
              }`}
            />
            {guide !== 'line' && <span className="bg-border absolute left-0 top-1/2 h-px w-2.5" />}
          </span>
        ),
      )}
    </>
  );
}

export interface CatalogRowProps {
  /** A fa-vezetővonalak (lapos listánál elhagyható). */
  guides?: readonly TreeGuide[];
  /** A sor felirata. */
  label: string;
  /** Kiemelt (vastag) felirat — a fa gyökér-csomópontja. */
  emphasized?: boolean;
  /** A felirat és a számláló közti jelzések (pl. ikon). */
  trailing?: ReactNode;
  /** Hány rekord hivatkozik a sorra (a számláló-badge); nincs → nem látszik. */
  count?: number;
  editLabel: string;
  deleteLabel: string;
  /** Nincs → nincs ceruza (jog hiányában). */
  onEdit?: () => void;
  /** Nincs → nincs kuka (jog hiányában). */
  onDelete?: () => void;
  /** A kuka tiltott (pl. használatban lévő típus) — a magyarázat a `deleteDisabledReason`-ben. */
  deleteDisabled?: boolean;
  deleteDisabledReason?: string;
}

/** Egy katalógus-sor: vezetővonalak · név · számláló · ceruza · kuka. */
export function CatalogRow({
  guides = [],
  label,
  emphasized = false,
  trailing,
  count,
  editLabel,
  deleteLabel,
  onEdit,
  onDelete,
  deleteDisabled = false,
  deleteDisabledReason,
}: CatalogRowProps): JSX.Element {
  return (
    <div className="hover:bg-sunken/40 flex items-center gap-2 rounded-lg px-2 py-1.5">
      <TreeGuideCells guides={guides} />
      <span
        className={`min-w-0 flex-1 truncate text-sm ${emphasized ? 'text-foreground font-medium' : ''}`}
      >
        {label}
      </span>
      {trailing}
      {count !== undefined && (
        <Badge variant="secondary" className="tabular-nums">
          {count}
        </Badge>
      )}
      {onEdit && (
        <Button variant="ghost" size="icon" type="button" aria-label={editLabel} onClick={onEdit}>
          <PencilSquareIcon className="size-4" />
        </Button>
      )}
      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          type="button"
          aria-label={deleteLabel}
          title={deleteDisabled ? deleteDisabledReason : undefined}
          disabled={deleteDisabled}
          onClick={onDelete}
        >
          <TrashIcon className="size-4" />
        </Button>
      )}
    </div>
  );
}

export interface CatalogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Van-e jog az írásra: nélküle nincs űrlap és nincs lábléc. */
  canManage: boolean;
  /** A react-hook-form `handleSubmit(...)` wrappere. */
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  busy: boolean;
  /** Szerkesztés alatt van-e egy sor (a gomb-feliratot és az űrlap-címet váltja). */
  editing: boolean;
  /** A szerkesztés elvetése (a „Mégse" gomb) — az űrlap kiürítése a hívó dolga. */
  onCancelEdit: () => void;
  /** A szerkesztésre váltáskor a sáv az űrlaphoz görget (minden váltáskor új érték). */
  scrollToFormKey: unknown;
  labels: { add: string; saveEdit: string; cancel: string; newTitle: string; editTitle: string };
  /** A lista tartalma (sorok); üres lista esetén az `emptyLabel` jelenik meg. */
  children: ReactNode;
  isEmpty: boolean;
  emptyLabel: string;
  /** A felvevő/szerkesztő űrlap mezői (a hívó `<Form>`-ba csomagolja). */
  form: ReactNode;
}

/**
 * A kezelő-sáv HÉJA: lista felül, alatta a felvevő/szerkesztő űrlap. A ceruza a lista bármelyik során állhat —
 * élesben 121 kategóriánál a sáv alján lévő űrlapot a kattintás után kézzel kellett megkeresni (user-lelet),
 * ezért a SZERKESZTÉSRE VÁLTÁS az űrlaphoz görget (a már újrarenderelt, előtöltött űrlaphoz — nem a kattintás-
 * kezelőben). A „Mégse" nem görget: ott a lista tetején maradni a helyes viselkedés.
 */
export function CatalogSheet({
  open,
  onOpenChange,
  title,
  description,
  canManage,
  onSubmit,
  busy,
  editing,
  onCancelEdit,
  scrollToFormKey,
  labels,
  children,
  isEmpty,
  emptyLabel,
  form,
}: CatalogSheetProps): JSX.Element {
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editing) return;
    formRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [editing, scrollToFormKey]);

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancelEdit();
        onOpenChange(o);
      }}
      title={title}
      description={description}
      onSubmit={canManage ? onSubmit : undefined}
      busy={busy}
      footer={
        canManage ? (
          <>
            {editing && (
              <Button variant="outline" type="button" onClick={onCancelEdit} disabled={busy}>
                {labels.cancel}
              </Button>
            )}
            <Button type="submit" variant="brand" disabled={busy}>
              {editing ? labels.saveEdit : labels.add}
            </Button>
          </>
        ) : undefined
      }
    >
      <div>
        {isEmpty ? (
          <Text variant="secondary" as="p" className="text-sm">
            {emptyLabel}
          </Text>
        ) : (
          children
        )}
      </div>
      {canManage && (
        <div ref={formRef} className="border-border/60 space-y-4 border-t pt-4">
          <Text variant="itemTitle" as="p">
            {editing ? labels.editTitle : labels.newTitle}
          </Text>
          {form}
        </div>
      )}
    </FormSheet>
  );
}

export interface CatalogDeleteDialogProps {
  /** A törlendő sor felirata; `null` → zárva. */
  targetLabel: string | null;
  onClose: () => void;
  title: string;
  /** A leírás a sor feliratával (a hívó i18n-je). */
  describe: (label: string) => string;
  pending: boolean;
  onConfirm: () => void;
}

/** A törlés-megerősítő a katalógus-sorokhoz (a `ConfirmDialog` közös feliratokkal). */
export function CatalogDeleteDialog({
  targetLabel,
  onClose,
  title,
  describe,
  pending,
  onConfirm,
}: CatalogDeleteDialogProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open={targetLabel !== null}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={title}
      description={describe(targetLabel ?? '')}
      confirmLabel={t('common.delete')}
      cancelLabel={t('common.cancel')}
      tone="danger"
      pending={pending}
      onConfirm={onConfirm}
    />
  );
}
